<template>
  <el-button link type="primary" :loading="loading" @click="openPreview">{{ resolvedName || buttonText }}</el-button>

  <el-dialog
    v-model="visible"
    :title="title"
    width="86%"
    top="4vh"
    append-to-body
    destroy-on-close
    class="finance-file-preview-dialog"
    @closed="releasePreview"
  >
    <div class="preview-body">
      <img v-if="previewKind === 'image'" :src="previewUrl" :alt="title" />
      <iframe v-else-if="previewKind === 'pdf'" :src="previewUrl" :title="title" />
      <p v-else>该文件暂不支持在线预览，请下载原文件查看。</p>
    </div>
    <template #footer>
      <el-button @click="downloadOriginal">下载原文件</el-button>
      <el-button type="primary" @click="visible = false">关闭</el-button>
    </template>
  </el-dialog>
</template>

<script setup>
import { detectPreviewFileKind, loadAuthorizedFileBlob } from '@/utils/upload'
import { loadFinanceFile, saveFileBlob } from '@/utils/finance-file'

const props = defineProps({
  fileRef: { type: [String, Number], required: true },
  title: { type: String, default: '文件预览' },
  buttonText: { type: String, default: '在线预览' },
  localFile: { type: Object, default: null },
  refundId: { type: String, default: '' },
  publicFile: { type: Boolean, default: false }
})

const { proxy } = getCurrentInstance()
const loading = ref(false)
const visible = ref(false)
const previewUrl = ref('')
const resolvedName = ref('')
let previewSequence = 0
const previewKind = ref('')
const originalFile = shallowRef(null)

watch(() => [props.fileRef, props.localFile, props.refundId], () => {
  previewSequence++; loading.value = false; resolvedName.value = ''
  visible.value = false
  releasePreview()
})

onBeforeUnmount(() => { previewSequence++; releasePreview() })

async function openPreview() {
  if (!props.fileRef || loading.value) return
  loading.value = true
  const sequence = ++previewSequence
  releasePreview()
  try {
    const file = props.localFile instanceof Blob
      ? { blob: props.localFile, fileName: props.localFile.name || props.buttonText || '附件' }
      : props.publicFile
      ? { blob: await loadAuthorizedFileBlob(props.fileRef), fileName: props.title }
      : await loadFinanceFile(props.fileRef, { refundId: props.refundId })
    const blob = file.blob
    const kind = await detectPreviewFileKind(blob)
    if (sequence !== previewSequence) return
    if (props.publicFile) file.fileName = `${props.title || '发票'}.${kind === 'pdf' ? 'pdf' : 'jpg'}`
    resolvedName.value = file.fileName
    originalFile.value = file
    previewKind.value = kind
    previewUrl.value = URL.createObjectURL(blob)
    visible.value = true
  } catch (error) {
    console.error(`${props.title || '文件'}加载失败`, error)
    if (sequence === previewSequence) proxy.$modal.msgError(error?.message || `${props.title || '文件'}加载失败`)
  } finally {
    if (sequence === previewSequence) loading.value = false
  }
}

function downloadOriginal() {
  if (!originalFile.value) return
  saveFileBlob(originalFile.value.blob, originalFile.value.fileName)
}

function releasePreview() {
  if (previewUrl.value) URL.revokeObjectURL(previewUrl.value)
  previewUrl.value = ''
  previewKind.value = ''
  originalFile.value = null
}
</script>

<style scoped>
.preview-body { display:flex; align-items:center; justify-content:center; height:calc(88vh - 130px); min-height:420px; overflow:auto; border:1px solid #dfe7ef; border-radius:6px; background:#f4f6f8; }
.preview-body img { display:block; max-width:100%; max-height:100%; object-fit:contain; }
.preview-body iframe { width:100%; height:100%; border:0; background:#fff; }
@media(max-width:768px){.preview-body{height:calc(90vh - 130px);min-height:300px}}
</style>
